import { CSSProperties, useEffect, useState } from "react"
import { Switch, Route, useLocation } from "react-router-dom"
import { NAV_BAR_HEIGHT_MOBILE, NAV_BAR_WIDTH_COLLAPSED, PlaybookType } from "modules/Const"
import useScreen from "Hooks/useScreen"
import { useNavbar } from "Hooks/useNavbar"
import useBrand from 'Hooks/useBrand'
import useOrganization from "Hooks/useOrganization"
import { styx } from "modules/Utils"

import SettingsPage from "pages/SettingsPage/SettingsPage"
import WebsiteHomepage from "pages/WebsitePage/Website"
import Dashboard from "pages/DashboardPage/DashboardPage"
import MarketingPage from "pages/MarketingPage/MarketingPage"
import Fans from "pages/FansPage/Fans";
import CreateNewSymphonyProfile from "pages/AddArtist/CreateNewSymphonyProfile"
import MarketingSuccessPage from "../pages/MarketingPage/SuccessPage"
import MarketingCampaignDetails from "pages/MarketingCampaignDetails/MarketingCampaignDetails"
import PreSaveProvider from "pages/MarketingPage/Data/PreSaveProvider"
import PreSave from "pages/MarketingPage/PreSave/PreSave"
import GetPlaylisted from "pages/MarketingPage/Playlisting"
import VideoViews from "pages/MarketingPage/VideoAds/VideoViews"
import CreativeGenerator from "pages/MarketingPage/CreativeGenerator"
import EnterEmail from "pages/EnterEmail/EnterEmail"
import ReferPage from "pages/Refer"
import AdminVideoViewsDataUpload from "pages/Admin/VideoViewsDataUpload"
import CampaignReports from "pages/Admin/reports"
import Learn from "pages/LearnPage/Learn"
import KPITrends from "pages/Admin/kpis"
import AffiliatesDashboard from "pages/Admin/AffiliatesDashboard"
import SourcesTrends from "pages/Admin/sources"
import TopPerforming from "pages/Admin/TopPerforming"
import UploadEmailsByCsv from "pages/FansPage/Components/UploadEmailsByCsv/UploadEmailsByCsv"
import OrganizationPage from "pages/OrganizationPage/OrganizationPage"
import AdminOnboarding from "pages/OrganizationPage/componentsOrganization/AdminOnboarding"
import ElementsPage from "pages/ElementsPage"
import TestApiPage from "pages/TestApiPage"
import TiktokCallbackPage from "pages/TikTokLoginCallback"
import CustomerSupportBrandEditingTools from "pages/CustomerSupportBrandEditingTools"
import MessageYourFans from "pages/FansPage/Components/MessageYourFans/MessageYourFans"
import EmailDetailsV2 from "pages/EmailDetailsV2"
import TablePage from "pages/TablePage"
import CreateNewWebsiteModal from "pages/WebsitePage/components/CreateNewWebsiteModal"
import Clipper from "pages/Admin/Clipper"
import AdCreatorDemo from "components/AdCreatorDemo"

import MarketingFlow2 from "components/MarketingFlow2"
import PaymentFailedModal from "components/shareable/PaymentFailedModal"
import CampaignSubmissionView from "components/CampaignSubmissionView"
import MaestroUserChat from "components/MaestroUserChat"
import OneClickCampaignModal from "components/OneClickCampaignModal"
import WebsiteBuilder from "components/websiteBuilder/WebsiteBuilder"
import OneClickCampaignSetupModal from "components/OneClickCampaignSetupModal"
import CreateNewCampaignModalV2 from "components/CreateNewCampaignModalV2"
import MaestroInstagramAnalyzer from "components/MaestroInstagramAnalyzer"
import CreateNewWebsiteModalV2 from "components/shareable/CreateNewWebsiteModalV2"
import WebsitePage from "pages/WebsitePage/components/WebsitePage"
import AllWebsites from "pages/AllWebsites"
import AddArtistV2 from "pages/AddArtistV2"
import OrganizationPageV2 from "pages/OrganizationPageV2"
import ApiKeysPage from "pages/ApiKeyPage"
import CampaignsDashboard from "pages/CampaignsDashboard"
import PaymentPastDueModal from "components/shareable/PaymentPastDueModal"

const PostAuthRoutes = () => {
    const location = useLocation()
    const { mStyle, mobileView, cssCalc } = useScreen()
    const {
        isCollapsed,
        shouldHideNavbar,
        isMobileNavbarShown,
        isWebsiteChildPage
    } = useNavbar();
    const { currentBrand, getIsPaymentFailed } = useBrand()
    const { organization } = useOrganization()

    const [showPaymentFailedModal, setShowPaymentFailedModal] = useState(getIsPaymentFailed())

    let postAuthRoutesWidth = '100%'

    if (!mobileView && isCollapsed) {
        // NOTE: if showing the navbar
        if (!shouldHideNavbar(location.pathname, organization, currentBrand)) {
            postAuthRoutesWidth = `calc(100% - ${NAV_BAR_WIDTH_COLLAPSED}px)`
        }
    }

    let overflowX = 'hidden'

    const getPostAuthRoutesHeight = () => {
        if (mobileView && isMobileNavbarShown()) {
            let heightOffset = NAV_BAR_HEIGHT_MOBILE

            if (location.pathname === '/' || location.pathname === '/dashboard') {
                heightOffset = NAV_BAR_HEIGHT_MOBILE + 49
            }

            return cssCalc(heightOffset)
        }

        return cssCalc()
    }

    if (isWebsiteChildPage()) {
        overflowX = 'visible'
    }

    useEffect(() => {
        setShowPaymentFailedModal(getIsPaymentFailed())
    }, [currentBrand?.id])

    return (
        <div
            id="main-body"
            className="postAuthRoutesComponent"
            style={styx(
                mStyle(sty, 'postAuthRoutesComponent'),
                { height: getPostAuthRoutesHeight() },
                { width: postAuthRoutesWidth },
                { overflowX: overflowX as CSSProperties['overflowX'] },
            )}
        >
            <Switch>
                <Route
                    path="/marketing2/new/:playbookType"
                    children={<MarketingFlow2 />}
                />
                <Route
                    path="/marketing2/draft/:draftId"
                    children={<MarketingFlow2 />}
                />
                <Route
                    path="/marketing2/submission/:campaignId"
                    children={<CampaignSubmissionView />}
                />
                <Route
                    exact
                    path="/email"
                    children={<EnterEmail />}
                />
                <Route
                    exact
                    path={["/", "/marketing"]}
                    children={<MarketingPage />}
                />
                <Route
                    exact
                    path="/dashboard"
                    children={<Dashboard />}
                />
                <Route
                    exact
                    path="/marketing/campaigns/:campaignId"
                    children={
                        <MarketingCampaignDetails
                            className="marketingCampaignDetailsComponentPostAuth"
                        />
                    }
                />
                <Route
                    exact
                    path="/marketing/new/creatives"
                    children={<CreativeGenerator />}
                />
                <Route
                    exact
                    path="/marketing/new/presave"
                    children={
                        <PreSaveProvider>
                            <PreSave setGoal={() => null} />
                        </PreSaveProvider>
                    }
                />
                <Route
                    exact
                    path="/marketing/new/music-streams"
                    children={
                        <MarketingFlow2
                            playbookType={PlaybookType.INCREASE_MUSIC_STREAMS}
                        />
                    }
                />
                <Route
                    exact
                    path="/marketing/new/grow-playlist-followers"
                    children={
                        <MarketingFlow2
                            playbookType={PlaybookType.GROW_PLAYLIST_FOLLOWERS}
                        />
                    }
                />
                <Route
                    exact
                    path="/marketing/new/grow-instagram-engagement"
                    children={
                        <MarketingFlow2
                            playbookType={PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT}
                        />
                    }
                />
                <Route
                    exact
                    path="/marketing/new/playlists"
                    children={<GetPlaylisted />}
                />
                <Route
                    exact
                    path="/marketing/new/video-views"
                    children={<VideoViews />}
                />

                <Route
                    exact
                    path="/marketing/new/tiktok-ads"
                    children={
                        <MarketingFlow2
                            playbookType={PlaybookType.TIKTOK_BOOST_ENGAGEMENT}
                        />
                    }
                />
                <Route
                    exact
                    path="/marketing/new/link-clicks"
                    children={
                        <MarketingFlow2
                            playbookType={PlaybookType.INCREASE_LINK_CLICKS}
                        />
                    }
                />
                <Route
                    path="/add-artist-OLD"
                    children={<CreateNewSymphonyProfile />}
                />
                <Route
                    path={["/add-artist", "/goals"]}
                    children={<AddArtistV2 />}
                />
                <Route
                    path="/marketing/success"
                    children={<MarketingSuccessPage />}
                />
                <Route
                    path="/tiktok/callback"
                    children={<TiktokCallbackPage />}
                />

                <Route
                    path="/website"
                    exact
                    children={<WebsitePage />}
                />
                <Route
                    path="/website-builder"
                    exact
                    children={<WebsiteBuilder />}
                />
                <Route
                    path="/website-builder/:brandSlug/:websiteSlug"
                    exact
                    children={<WebsiteBuilder />}
                />
                <Route
                    path="/fans/campaigns/:id"
                    // children={<FanbaseCampaignDetails />}
                    children={<EmailDetailsV2 />}
                />
                <Route
                    path="/fans"
                    children={<Fans />}
                />
                <Route
                    path="/import-fans"
                    children={<UploadEmailsByCsv />}
                />

                <Route
                    path="/message-your-fans"
                    children={<MessageYourFans />}
                />

                <Route
                    path="/website/:websiteType/:websiteSlug"
                    children={<WebsiteHomepage />}
                />

                <Route
                    path="/elementspage"
                    exact
                    children={<ElementsPage />}
                />
                <Route
                    path="/testapi"
                    exact
                    children={<TestApiPage />}
                />
                <Route
                    path="/admintools"
                    exact
                    children={<CustomerSupportBrandEditingTools />}
                />

                <Route
                    path="/allwebsites"
                    exact
                    children={<AllWebsites />}
                />

                <Route
                    path="/learn"
                    children={<Learn />}
                />

                <Route
                    path="/settings"
                    exact
                    children={<SettingsPage />}
                />

                {/* <Route
                    path="/team"
                    exact
                    children={<OrganizationPage />}
                /> */}
                {/* NOTE CONNER: put back when using V2! */}
                <Route
                    path="/team"
                    exact
                    children={<OrganizationPageV2 />}
                />
                <Route
                    path="/admin/onboarding"
                    exact
                    children={<AdminOnboarding />}
                />
                <Route
                    path="/refer"
                    exact
                    children={<ReferPage />}
                />
                <Route
                    path="/admin"
                    exact
                    children={<AdminVideoViewsDataUpload />}
                />
                <Route
                    path="/admin/reports"
                    exact
                    children={<CampaignReports />}
                />
                <Route
                    path="/admin/top-performing"
                    exact
                    children={<TopPerforming />}
                />
                <Route
                    path="/admin/kpis"
                    exact
                    children={<KPITrends />}
                />
                <Route
                    path="/admin/sources"
                    exact
                    children={<SourcesTrends />}
                />
                <Route
                    path="/admin/affiliates"
                    exact
                    children={<AffiliatesDashboard />}
                />

                <Route
                    path="/tools/clipper"
                    exact
                    children={<Clipper />}
                />

                <Route
                    path="/tools/ad-creator"
                    exact
                    children={<AdCreatorDemo />}
                />

                <Route
                    path="/table"
                    exact
                    children={<TablePage />}
                />
                <Route
                    path="/api-keys"
                    exact
                    children={<ApiKeysPage />}
                />
                <Route
                    path="/campaigns-dashboard"
                    exact
                    children={<CampaignsDashboard />}
                />
            </Switch>
            {/* <PaymentFailedModal
            className="paymentFailedModalPostAuthRoutes"
            isOpen={showPaymentFailedModal}
            onClose={() => setShowPaymentFailedModal(false)}
        /> */}
            <PaymentPastDueModal autoShow={true} />

            {currentBrand?.id &&
                <>
                    {/* useMaestroChatStore.ts */}
                    <MaestroUserChat
                        className="maestroUserChatPostAuthRoutes"
                    />

                    {/* useCampaign.ts */}
                    <OneClickCampaignSetupModal
                        className="oneClickCampaignSetupModalPostAuthRoutes"
                    />
                    <CreateNewCampaignModalV2
                        className="createNewCampaignModalPostAuthRoutesV2"
                    />
                    <OneClickCampaignModal
                        className="oneClickCampaignModalPostAuthRoutes"
                    />

                    {/* useMaestroInstagramPosts.ts */}
                    <MaestroInstagramAnalyzer
                        className="maestroInstagramAnalyzerPostAuthRoutes"
                    />

                    {/* useWebsiteBuilder.ts */}
                    {/* <CreateNewWebsiteModal
                        className="createNewWebsiteModalPostAuthRoutes"
                    /> */}
                    <CreateNewWebsiteModalV2
                        className="createNewWebsiteModalPostAuthRoutesV2"
                    />
                </>
            }
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    postAuthRoutesComponent: {
        display: "flex",
        flexDirection: "column",
        marginLeft: 'auto',
    },
    postAuthRoutesComponent_mobile: {
        marginLeft: 0,
    }
}

export default PostAuthRoutes